import { useEffect, useRef, useMemo } from "react";
import * as signalR from "@microsoft/signalr";
import IMeeting from "../../api/IMeeting";
import CONSTANTS from "../../contracts/Constants";
import { useMeetingStore } from "../../stores/useMeetingStore";
import stringify, { toString } from "../../utilities/stringify";
import useHubConnection from "./useHubConnection";
import useJWTCookie from "./useJWTCookie";

type UseMeetingChangeSocket = {
    meetingId: string;
    onReconnected?: () => Promise<void> | void;
    onHeartbeatFailed?: (failureCount: number) => void;
    onMeetingEnded?: () => void;
};

const useMeetingChangeSocket = ({ 
    meetingId, 
    onReconnected,
    onHeartbeatFailed,
    onMeetingEnded
}: UseMeetingChangeSocket) => {
    const setMeeting = useMeetingStore((state) => state.setMeeting);
    const heartbeatMonitorRef = useRef<NodeJS.Timeout | null>(null);
    const { getJwt } = useJWTCookie();

    /**
     * 获取有效的JWT令牌
     */
    const getAccessToken = useRef(async (): Promise<string> => {
        try {
            const [jwtToken, expirationDateString] = await getJwt();
            
            console.log(`[Meeting] getJwt returned - token exists: ${!!jwtToken}, expiration: ${expirationDateString}`);
            
            if (!jwtToken || jwtToken.trim() === "") {
                console.warn("[Meeting] ⚠️ No JWT token found in storage - user may not be logged in");
                return "";
            }
            
            // 检查令牌是否过期
            const expirationDate = expirationDateString ? parseInt(expirationDateString as any) : null;
            const now = new Date().getTime();
            
            if (expirationDate && now > expirationDate) {
                console.warn(`[Meeting] ⚠️ JWT token has expired - expiration: ${expirationDate}, now: ${now}`);
                return "";
            }
            
            console.log("[Meeting] ✓ JWT token successfully retrieved for SignalR connection");
            return jwtToken;
        } catch (error) {
            console.error("[Meeting] ❌ Failed to retrieve JWT token:", error);
            return "";
        }
    }).current;

    /**
     * 缓存 useHubConnection 的配置选项，防止每次重新渲染时创建新对象
     * 这样 useHubConnection 的依赖项不会频繁改变，从而避免不必要的重新连接
     */
    const hubConnectionOptions = useMemo(() => ({
        getAccessToken: getAccessToken  // 传递JWT获取函数
    }), [getAccessToken]);

    /**
     * 缓存连接回调函数
     */
    const onConnection = (hubConnection: signalR.HubConnection) => {
        hubConnection.on("MeetingChanged", onMeetingChange);
        hubConnection.on("MeetingEnded", onMeetingEndedHandler);
        console.log(`[Meeting] Setting up SignalR listeners for meeting: ${meetingId}`);
    };

    const onConnectionMemo = useMemo(() => onConnection, [meetingId]);

    /**
     * 缓存连接建立回调
     */
    const onStartMemo = useMemo(() => () => {
        // 连接建立时启动心跳监控
        startHeartbeatMonitor();
    }, []);

    /**
     * 心跳配置：
     * - 每30秒发送一次心跳
     * - 5秒无响应视为超时
     * - 连续失败3次后触发重连
     */
    const hubConnectionRef = useHubConnection(
        CONSTANTS.SocketUrl + `?meetingId=${meetingId}`,
        onConnectionMemo,
        onStartMemo,
        {
            interval: 30000,      // 30秒心跳间隔
            timeout: 5000,        // 5秒超时
            maxFailures: 3,       // 3次失败后重连
        },
        hubConnectionOptions  // 使用缓存的 options
    );

    /**
     * 启动心跳监控（监听器）
     */
    const startHeartbeatMonitor = () => {
        // 每20秒检查一次连接健康状态
        heartbeatMonitorRef.current = setInterval(() => {
            if (onHeartbeatFailed && hubConnectionRef.current) {
                // 这里可以扩展来获取心跳状态
                // 例如通过在useHubConnection中暴露getConnectionHealth方法
                console.log("[Meeting] Heartbeat monitor running...");
            }
        }, 20000);
    };

    /**
     * 停止心跳监控
     */
    const stopHeartbeatMonitor = () => {
        if (heartbeatMonitorRef.current) {
            clearInterval(heartbeatMonitorRef.current);
            heartbeatMonitorRef.current = null;
        }
    };

    const onMeetingChange = (changedId: string, newMeeting: IMeeting) => {
        console.log(`[Meeting] MeetingChanged event received for meeting: ${changedId}`);
        
        // 验证1：检查meetingId是否为空（可能是心跳响应）
        if (!changedId || changedId === "00000000-0000-0000-0000-000000000000") {
            console.log(`[Meeting] Ignoring event - invalid meeting ID received (likely heartbeat response)`);
            return;
        }
        
        // 验证2：检查meetingId是否匹配
        if (meetingId !== changedId) {
            console.warn(`[Meeting] Meeting ID mismatch. Expected: ${meetingId}, Received: ${changedId}`);
            return;
        }
        
        // 验证3：检查新数据是否为null（可能是空的心跳响应）
        if (!newMeeting) {
            console.log(`[Meeting] Ignoring event - meeting data is null`);
            return;
        }

        console.log(`[Meeting] Updating meeting state with new data:`, {
            votingRound: newMeeting.votingRound,
            votingOn: newMeeting.votingOn,
            participantsCount: newMeeting.participants?.length
        });

        setMeeting(newMeeting);
    };

    /**
     * 处理会议结束事件
     */
    const onMeetingEndedHandler = (endedMeetingId: string) => {
        console.log(`[Meeting] MeetingEnded event received for meeting: ${endedMeetingId}`);
        
        if (meetingId !== endedMeetingId) {
            console.warn(`[Meeting] Meeting ID mismatch in MeetingEnded event. Expected: ${meetingId}, Received: ${endedMeetingId}`);
            return;
        }

        console.log(`[Meeting] Meeting ${meetingId} has ended. All participants should exit.`);
        if (onMeetingEnded) {
            onMeetingEnded();
        }
    };

    // 监听重连事件并恢复参与者状态
    useEffect(() => {
        if (!hubConnectionRef.current) return;

        const hubConnection = hubConnectionRef.current;

        // 注册重连事件处理器
        hubConnection.onreconnected(async (connectionId?: string) => {
            console.log(`[Meeting] SignalR reconnected with connectionId: ${connectionId}`);
            
            // 重新启动心跳监控
            startHeartbeatMonitor();

            // 重连后调用外部的恢复函数（如重新加入会议）
            if (onReconnected) {
                try {
                    await onReconnected();
                    console.log(`[Meeting] Successfully recovered participant state after reconnection`);
                } catch (error) {
                    console.error(`[Meeting] Failed to recover state after reconnection:`, error);
                }
            }
        });

        return () => {
            stopHeartbeatMonitor();
        };
    }, [meetingId, onReconnected, onHeartbeatFailed, onMeetingEnded]);

    // 清理
    useEffect(() => {
        return () => {
            stopHeartbeatMonitor();
            console.log(`[Meeting] Cleaning up SignalR listeners for meeting: ${meetingId}`);
        };
    }, [meetingId]);

    return;
};

export default useMeetingChangeSocket;
